import { useMemo } from 'react';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { Trans, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { useFetchMessagingROIOOverview } from '../../hooks/use-fetch-messaging-roi-overview';
import { MessagingOverview, RoiBaseFilters } from '../../types';
import { MultiOverview } from '../multi-overview';
import { MultiComparisonChart } from './charts/multi-comparison-chart';

interface Props extends ReturnType<typeof useFetchMessagingROIOOverview> {
  filters?: RoiBaseFilters;
  overviewData: MessagingOverview;
}

export const MessagingMultiSubView = ({ filters, overviewData, ...rest }: Props) => {
  const { t } = useTranslation('analytics');
  const search = useSearch({ strict: false }) ?? { view: 'leadsContacted' };
  const locationsCount = filters?.locationIds?.length || 0;
  const { averageProductionAmount } = AnalyticsHooks.useAverageProductionAmount();

  const overview = useMemo(() => {
    let title = '';
    let value = '0';
    let message = <Trans t={t}></Trans>;

    switch (search.view) {
      case 'leadsContacted':
        title = t('Leads Contacted');
        value = formatters.value(overviewData.leadsContacted.totalPatients);
        message = (
          <Trans t={t} values={{ messages: overviewData.leadsContacted.messagesSent, locationsCount }}>
            Patients were contacted through{' '}
            <strong style={{ fontSize: theme.fontSize(20) }}>
              {{ messages: overviewData.leadsContacted.messagesSent }}
            </strong>{' '}
            messages sent across {{ locationsCount }} Locations
          </Trans>
        );
        break;

      case 'patientsScheduled':
        title = t('Patients Scheduled');
        value = formatters.value(overviewData.patientsScheduled.totalPatients);
        message = (
          <Trans
            t={t}
            values={{ appointments: overviewData.patientsScheduled.appointmentsScheduledCount, locationsCount }}
          >
            Patients scheduled{' '}
            <strong style={{ fontSize: theme.fontSize(20) }}>
              {{ appointments: overviewData.patientsScheduled.appointmentsScheduledCount }}
            </strong>{' '}
            appointments across {{ locationsCount }} Locations
          </Trans>
        );
        break;

      case 'revenueGenerated':
        title = t('Revenue Generated');
        value = formatters.currency(overviewData.revenueGenerated.totalCompletedAppts * averageProductionAmount.value);
        message = (
          <Trans
            t={t}
            values={{ appointmentsCompleted: overviewData.revenueGenerated.totalCompletedAppts, locationsCount }}
          >
            <strong style={{ fontSize: theme.fontSize(20) }}>
              {{ appointmentsCompleted: overviewData.revenueGenerated.totalCompletedAppts }}
            </strong>{' '}
            appointments were completed across {{ locationsCount }} Locations
          </Trans>
        );
    }

    return {
      message,
      title,
      value,
    };
  }, [overviewData, locationsCount, search.view, averageProductionAmount, t]);

  return (
    <>
      <MultiOverview message={overview.message} value={overview.value} />
      <MultiComparisonChart {...rest} title={overview.title} />
    </>
  );
};
